import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import { PaginationType } from '@.api/methods';
import { createDefaultPagination, createSelectors, debounce } from '@/Lib';

import { ClusterStoreDetailService } from './ClusterStoreDetailService';
import { ClusterStoreListModel } from './ClusterStoreListModel';
import { ClusterStoreModel } from './ClusterStoreModel';

interface ClusterStoreDetailState {
    cluster: ClusterStoreModel | null;
    stores: ClusterStoreListModel[];

    pagination: PaginationType;

    searchStoreName: string;

    /**
     * TODO: https://redmine.cyberia.studio/issues/29664 need implement after complete api.
     */
    clusterUser: null;
}

interface ClusterStoreDetailActions {
    setPagination: (pagination: Partial<PaginationType>) => void;
    changeSearchStoreName: (name: string) => void;

    fetchClusterStores: () => Promise<void>;
    fetchClusterStoresDebounce: () => void;
    setSpClusterDetail: (spCluster: ClusterStoreModel) => void;

    resetState: () => void;
}

type ClusterStoreDetailStore = ClusterStoreDetailState & ClusterStoreDetailActions;

const createInitialClusterStoreDetailState = (): ClusterStoreDetailState => {
    return {
        cluster: null,
        stores: [],
        pagination: createDefaultPagination(),
        searchStoreName: '',
        clusterUser: null,
    };
};

export const clusterStoreDetailStore = create<ClusterStoreDetailStore>()(
    immer<ClusterStoreDetailStore>((set, getState, store) => ({
        ...createInitialClusterStoreDetailState(),
        resetState: () => {
            set((state) => {
                const initialState = createInitialClusterStoreDetailState();

                state.cluster = initialState.cluster;
                state.stores = initialState.stores;
                state.pagination = initialState.pagination;
                state.searchStoreName = initialState.searchStoreName;
                state.clusterUser = initialState.clusterUser;
            });
        },
        setPagination: (pagination: Partial<PaginationType>) => {
            set((state) => {
                state.pagination = {
                    ...state.pagination,
                    ...pagination,
                };
            });
        },
        fetchClusterStores: async () => {
            const state = getState();

            if (!state.cluster?.clusterId) {
                return;
            }

            const clusterStoreDetailService = new ClusterStoreDetailService();

            const result = await clusterStoreDetailService.getClusterStores(state.cluster.clusterId, state.pagination.page, state.searchStoreName);

            set((state) => {
                state.stores = result.stores;

                state.setPagination(result.pagination);
            });
        },
        fetchClusterStoresDebounce: debounce(async () => {
            const state = getState();

            state.fetchClusterStores();
        }, 300),
        changeSearchStoreName: (name: string) => {
            const state = getState();

            set((state) => {
                state.searchStoreName = name;
            });

            state.fetchClusterStoresDebounce();
        },
        setSpClusterDetail: (spCluster: ClusterStoreModel) => {
            set((state) => {
                state.cluster = spCluster;
            });
        },
    }))
);

export const storeClusterDetailStoreSelectors = createSelectors(clusterStoreDetailStore);
